<template>
  <footer class="app-footer">
    <div class="footer-glow" aria-hidden="true"></div>
    <div class="container footer-inner">
      <div class="footer-col footer-brand">
        <AppLogo/>
        <p>{{ $t('footer.tagline') }}</p>
      </div>

      <div class="footer-col">
        <h4>{{ $t('footer.support') }}</h4>
        <NuxtLink :to="localePath('/contact')">{{ $t('contact') }}</NuxtLink>
        <NuxtLink :to="localePath('/faq')">FAQ</NuxtLink>
        <NuxtLink :to="localePath('/terms')">{{ $t('terms.title') || 'Terms' }}</NuxtLink>
        <NuxtLink :to="localePath('/imprint')">{{ $t('imprint.title') || 'Imprint' }}</NuxtLink>
        <NuxtLink :to="localePath('/datenschutz')">{{ $t('privacy.title', 'Privacy Policy') }}</NuxtLink>
        <NuxtLink :to="localePath('/data-deletion')">{{ $t('dataDeletion.title', 'Data deletion') }}</NuxtLink>
        <NuxtLink :to="localePath('/widerrufsrecht')">{{ $t('shop.withdrawal_policy', 'Right of withdrawal') }}</NuxtLink>
        <NuxtLink :to="localePath('/versand')">{{ $t('shop.shipping_policy', 'Shipping') }}</NuxtLink>
        <NuxtLink :to="localePath('/rueckgabe')">{{ $t('shop.return_policy', 'Returns') }}</NuxtLink>
        <a href="#" @click.prevent="openCookieSettings">{{ $t('cookies.settings') }}</a>
      </div>

      <div v-if="isShop" class="footer-col">
        <h4>{{ $t('shop.newsletter_heading', 'Newsletter') }}</h4>
        <ShopNewsletterSignup
          variant="footer"
          source="footer"
          :subtitle="$t('shop.newsletter_footer_sub', 'New releases and free downloads, no spam.')"
        />
      </div>

      <div v-if="socialLinks.length" class="footer-col footer-social">
        <h4>{{ $t('footer.follow_us') }}</h4>
        <div class="social-icons">
          <a
            v-for="item in socialLinks"
            :key="item.key"
            :href="item.url"
            :aria-label="item.label"
            target="_blank"
            rel="noopener noreferrer"
            class="social-btn"
          >
            <NuxtIcon :name="item.icon" size="20" />
          </a>
        </div>
      </div>
    </div>

    <div class="footer-bottom container">
      <p class="copy">&copy; {{ year }} {{ appName }}. {{ $t('footer.copyright') }}</p>
      <p v-if="isDiztreeBrand" class="made-by">
        Made with <span class="heart">❤</span> in Hamburg
      </p>
      <p v-else class="made-by">
        Made with <span class="music">🎵</span> on the speakers by <a href="https://diztree.com" target="_blank" rel="noopener noreferrer">Diztree</a>
      </p>
    </div>
  </footer>
</template>

<script setup>
const localePath = useLocalePath()
const { appName, socials, isDiztreeBrand } = useTenantTheme()
const { show: openCookieSettings } = useCookieConsent()

const year = new Date().getFullYear()

// The footer renders on every subdomain (admin, creator, …); a customer
// newsletter box only belongs on the storefront. Route-based rather than
// host-based so it also behaves in dev, where everything is one origin.
const route = useRoute()
const isShop = computed(() =>
  !route.path.startsWith('/admin')
  && !route.path.startsWith('/creator')
  && !route.path.startsWith('/labels')
  && !route.path.startsWith('/superadmin')
  && !route.path.startsWith('/scouts')
)

const socialIconMap = {
  instagram: { icon: 'mdi:instagram', label: 'Instagram' },
  tiktok: { icon: 'simple-icons:tiktok', label: 'TikTok' },
  youtube: { icon: 'mdi:youtube', label: 'YouTube' },
  twitter: { icon: 'simple-icons:x', label: 'X / Twitter' },
  soundcloud: { icon: 'mdi:soundcloud', label: 'SoundCloud' },
  spotify: { icon: 'mdi:spotify', label: 'Spotify' },
  discord: { icon: 'simple-icons:discord', label: 'Discord' },
  facebook: { icon: 'mdi:facebook', label: 'Facebook' }
}

const socialLinks = computed(() => {
  const s = socials.value || {}
  return Object.entries(socialIconMap)
    .filter(([key]) => !!s[key])
    .map(([key, meta]) => ({ key, url: s[key], ...meta }))
})
</script>

<style scoped>
.app-footer {
  position: relative;
  background: linear-gradient(180deg, rgb(var(--surface-channels, 13 14 18) / 0.6) 0%, var(--surface-color) 60%, #000 100%);
  border-top: 1px solid var(--border);
  /* bottom safe-area: keep the copyright line above the iOS home indicator */
  padding: var(--space-12) 0 calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
  margin-top: auto;
  overflow: hidden;
}

.footer-glow {
  position: absolute;
  top: -80px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 160px;
  background: radial-gradient(ellipse at center, rgb(var(--primary-channels) / 0.18), transparent 70%);
  filter: blur(40px);
  pointer-events: none;
  z-index: 0;
}

.footer-inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: var(--space-10);
  margin-bottom: var(--space-10);
  align-items: start;
}

.footer-brand :deep(img),
.footer-brand :deep(svg) {
  margin-bottom: var(--space-4);
}

.footer-col h4 {
  margin-bottom: var(--space-4);
  color: var(--text-main);
  font-family: var(--font-heading);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.footer-col p {
  color: var(--text-muted);
  font-size: 0.9rem;
  line-height: 1.6;
  max-width: 320px;
  margin-top: var(--space-3);
}

/* Text links in the footer columns. Exclude `.social-btn` so the
   icon row keeps its inline-flex layout, zero margins, and lift-on-
   hover transform from the `.social-btn` rules below — otherwise
   `.footer-col a` wins on specificity (class + element) over the
   plain `.social-btn` class and the icons stack vertically with
   slide-right hover instead of sitting inline. */
.footer-col a:not(.social-btn) {
  display: block;
  color: var(--text-muted);
  /* base = touch: generous padding stretches each row toward the 44px
     tap-target minimum; tightened again for pointer devices below. */
  padding: 10px 0;
  margin-bottom: 0;
  font-size: 0.9rem;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;
}

/* compact rows only for real pointer devices — width alone would give
   touch-tablets sub-44px targets */
@media (hover: hover) and (pointer: fine) {
  .footer-col a:not(.social-btn) {
    padding: 2px 0;
    margin-bottom: var(--space-2);
  }
}

.footer-col a:not(.social-btn):focus-visible,
.social-btn:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.footer-col a:not(.social-btn):hover {
  color: var(--primary);
  transform: translateX(3px);
}

.footer-social .social-icons {
  display: flex;
  gap: var(--space-3);
  margin-top: 0;
}

.social-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0;
  border-radius: 10px;
  background: rgb(var(--primary-channels) / 0.06);
  border: 1px solid rgb(var(--primary-channels) / 0.15);
  color: var(--text-muted);
  transition: all 0.22s ease;
}

.social-btn:hover {
  color: #fff;
  background: linear-gradient(135deg, var(--primary), var(--secondary, var(--primary)));
  border-color: transparent;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgb(var(--primary-channels) / 0.35);
}

.footer-bottom {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  padding-top: var(--space-6);
  border-top: 1px solid var(--border-active);
  /* --text-muted, not --text-dim: dim is ~2.8:1 on this background and
     fails WCAG AA for real copy like the copyright line. */
  color: var(--text-muted);
  font-size: 0.82rem;
}

.footer-bottom .copy {
  margin: 0;
}

.made-by {
  margin: 0;
}

.made-by a {
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;
}

.made-by a:hover {
  text-decoration: underline;
}

.heart {
  color: #ff4d6a;
  display: inline-block;
  animation: heartbeat 1.8s ease-in-out infinite;
}

@keyframes heartbeat {
  0%, 100% { transform: scale(1); }
  15% { transform: scale(1.15); }
  30% { transform: scale(1); }
  45% { transform: scale(1.15); }
}

@media (max-width: 767px) {
  .footer-inner {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }

  .footer-bottom {
    flex-direction: column;
    text-align: center;
  }
}
</style>
